import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  faFloppyDisk,
  faUserHelmetSafety,
} from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Col, Row, Space, theme, Typography } from 'antd';
import dayjs from 'dayjs';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { useJobTechnicians } from 'pos-client/contexts/JobTechniciansContext.tsx';
import { useOrderOutletContext } from 'pos-client/contexts/OutletContexts.ts';
import { useLocationLinkedTechnicians } from 'pos-client/hooks/useLocationLinkedTechnicians.ts';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { OrderJobTechnicianFeModel } from 'pos-shared/models/orderSchema.ts';

import { TechniciansTable } from './TechniciansTable.tsx';
import { useConfirmNonScheduledTechnician } from './useConfirmNonScheduledTechnician.tsx';

export type OrderTechniciansModalType = {
  jobId: string;
  jobType: string;
  jobStartTime: string;
  jobEndTime: string;
  serviceLocationId: string;
  technicians: OrderJobTechnicianFeModel[];
  visible: boolean;
};

type OrderTechniciansModalProps = OrderTechniciansModalType & {
  onSetEditTechniciansModal: (
    technicianModalProps: OrderTechniciansModalType
  ) => void;
};

export const OrderTechniciansModal = memo(
  ({
    jobId,
    jobType,
    jobStartTime,
    jobEndTime,
    serviceLocationId,
    onSetEditTechniciansModal,
  }: OrderTechniciansModalProps) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const trpcContext = trpc.useUtils();
    const { confirmNonScheduledTechnician } =
      useConfirmNonScheduledTechnician();
    const { showSuccess, showError } = useNotifications();
    const {
      technicians,
      newTechnicians,
      updatedTechnicians,
      deletedTechnicians,
      addTechnician,
    } = useJobTechnicians();
    const { selectedOrderDetailsForAssignTechnician, jobDetailsModalData } =
      useScheduleBoardContext();

    const { orderId } = useOrderOutletContext() ?? '';
    // We probably need a refactor to improve where orderId is coming from
    const currentOrderId =
      selectedOrderDetailsForAssignTechnician.orderId ||
      jobDetailsModalData?.orderId ||
      orderId;
    const parseTime = (time: string, format: string) => {
      // Check for time format of 2024-07-29T09:54:00-0400
      const checkForTimezone = (
        jobDetailsModalData?.startDate.match(/-|\+/g) || []
      ).length;
      // remove -400 (timezone) to prevent dayjs changing the slot time
      const parsedTime = checkForTimezone === 3 ? time.slice(0, -5) : time;
      // Return time in format correct for technicians endpoint in UTC as schedules are all UTC but localised
      // EG. 5pm -400 in schedules is just 5pm and not 1pm
      return dayjs(parsedTime, format).utc(true).format('YYYY-MM-DDTHH:mm:ss');
    };

    const startTime =
      (jobDetailsModalData?.startDate &&
        parseTime(jobDetailsModalData.startDate, 'YYYY-MM-DDTHH:mm:ss')) ??
      parseTime(jobStartTime, 'DD/MM/YYYY HH:mm:ss') ??
      undefined;
    const endTime =
      (jobDetailsModalData?.endDate &&
        parseTime(jobDetailsModalData.endDate, 'YYYY-MM-DDTHH:mm:ss')) ??
      parseTime(jobEndTime, 'DD/MM/YYYY HH:mm:ss') ??
      undefined;

    const isFormDirty = useMemo(() => {
      return (
        newTechnicians.length > 0 ||
        updatedTechnicians.length > 0 ||
        deletedTechnicians.length > 0
      );
    }, [newTechnicians, updatedTechnicians, deletedTechnicians]);

    const [showAlert, setShowAlert] = useState(false);

    const {
      technicians: technicianList,
      isLoadingTechnicians: isLoadingTechnicianList,
    } = useLocationLinkedTechnicians({
      serviceLocationId,
      startTime: startTime,
      endTime: endTime,
      assigningToAJob: true,
    });

    const technicianNotScheduled = useMemo(() => {
      return technicians.some((tech) => {
        return technicianList.nonScheduledTechnicians.find(
          (technician) => technician.id === tech.userId
        );
      });
    }, [technicianList.nonScheduledTechnicians, technicians]);

    const onCancelEditTechnicians = useCallback(() => {
      onSetEditTechniciansModal({
        jobId: '',
        jobType: '',
        jobStartTime: '',
        jobEndTime: '',
        serviceLocationId: '',
        technicians: [],
        visible: false,
      });
    }, [onSetEditTechniciansModal]);

    const {
      mutateAsync: addOrderJobFulfilmentTechnician,
      isLoading: addingFulfilmentTechnician,
    } = trpc.orders.addOrderJobFulfilmentTechnician.useMutation();

    const {
      mutateAsync: updateOrderJobFulfilmentTechnician,
      isLoading: updatingFulfilmentTechnician,
    } = trpc.orders.updateOrderJobFulfilmentTechnician.useMutation();

    const {
      mutate: deleteOrderJobFulfilmentTechnician,
      isLoading: isDeletingFulfilmentTechnician,
    } = trpc.orders.deleteOrderJobFulfilmentTechnician.useMutation();

    const isSavingChanges =
      addingFulfilmentTechnician ||
      updatingFulfilmentTechnician ||
      isDeletingFulfilmentTechnician;

    const onSaveEditTechnicians = useCallback(async () => {
      let saveEditTechnicianFailed = false;
      let saveEditTechnicianError = '';

      const handleSuccess = async () => {
        await trpcContext.orders.getOrder.invalidate({
          id: currentOrderId,
        });

        await trpcContext.getOrderJobFulfilmentTechnicians.invalidate({
          orderId: currentOrderId,
        });

        await trpcContext.getConflictLanes.invalidate();

        await trpcContext.findScheduleBoardJobsAndBlockedSlots.invalidate();

        if (!saveEditTechnicianFailed) {
          showSuccess(t('technicians.updateTechniciansSuccess'));
          onCancelEditTechnicians();
        } else handleError(saveEditTechnicianError);
        if (jobDetailsModalData?.orderId) {
          trpcContext.getOrderJobFulfilmentTechnicians.invalidate({
            orderId: currentOrderId,
            jobId: jobDetailsModalData?.jobId,
          });
        }
      };

      const handleError = (error: string) => {
        saveEditTechnicianFailed = true;
        showError(`${t('technicians.updateTechniciansError')} - ${error}`);
      };

      for (const [index, tech] of updatedTechnicians.entries()) {
        await updateOrderJobFulfilmentTechnician(
          {
            orderId: currentOrderId,
            jobId,
            technician: {
              ...tech,
              jobType,
              isDeleted: false,
            },
          },
          {
            onSuccess: () => {
              if (
                index === updatedTechnicians.length - 1 &&
                newTechnicians.length === 0 &&
                deletedTechnicians.length === 0 &&
                !saveEditTechnicianFailed
              ) {
                handleSuccess();
              }
            },
            onError: (error) => {
              saveEditTechnicianFailed = true;
              saveEditTechnicianError = error.message;
              if (
                index === updatedTechnicians.length - 1 &&
                newTechnicians.length === 0 &&
                deletedTechnicians.length === 0
              ) {
                handleError(saveEditTechnicianError);
              }
            },
          }
        );
      }

      for (const [index, tech] of newTechnicians.entries()) {
        await addOrderJobFulfilmentTechnician(
          {
            orderId: currentOrderId,
            jobId,
            technician: {
              ...tech,
              jobType: jobType,
            },
          },
          {
            onSuccess: () => {
              if (
                index === newTechnicians.length - 1 &&
                deletedTechnicians.length === 0 &&
                !saveEditTechnicianFailed
              ) {
                handleSuccess();
              }
            },
            onError: (error) => {
              saveEditTechnicianFailed = true;
              saveEditTechnicianError = error.message;
              if (
                index === newTechnicians.length - 1 &&
                deletedTechnicians.length === 0
              ) {
                handleError(saveEditTechnicianError);
              }
            },
          }
        );
      }

      deletedTechnicians.forEach((tech) => {
        deleteOrderJobFulfilmentTechnician(
          {
            orderId: currentOrderId,
            jobId,
            userId: tech.userId,
          },
          {
            onSuccess: handleSuccess,
            onError: (error) => handleError(error.message),
          }
        );
      });
    }, [
      deletedTechnicians,
      trpcContext.orders.getOrder,
      trpcContext.getOrderJobFulfilmentTechnicians,
      trpcContext.getConflictLanes,
      trpcContext.findScheduleBoardJobsAndBlockedSlots,
      currentOrderId,
      jobDetailsModalData?.orderId,
      jobDetailsModalData?.jobId,
      showSuccess,
      t,
      onCancelEditTechnicians,
      showError,
      updatedTechnicians,
      updateOrderJobFulfilmentTechnician,
      jobId,
      jobType,
      newTechnicians,
      addOrderJobFulfilmentTechnician,
      deleteOrderJobFulfilmentTechnician,
    ]);

    const confirmHandler = () => {
      if (technicianNotScheduled) {
        const techniciansBeingAssigned =
          technicianList.nonScheduledTechnicians.filter((technician) => {
            return newTechnicians.some(
              (newTechnician) => technician.id === newTechnician.userId
            );
          });
        confirmNonScheduledTechnician(
          () => handleSaveTechnicians(),
          techniciansBeingAssigned
        );
      } else {
        handleSaveTechnicians();
      }
    };

    const handleSaveTechnicians = () => {
      setShowAlert(false);

      if (technicians.length) {
        const isEffortMet =
          technicians.reduce(
            (total, tech) => total + (tech.effortPercentage ?? 0),
            0
          ) === 100;

        if (!isEffortMet) {
          setShowAlert(true);

          return;
        }
      }

      onSaveEditTechnicians();
    };

    const onAddNewTechnicianRow = useCallback(
      () =>
        addTechnician({
          userId: `new${newTechnicians.length + 1}`,
          name: '',
          effortPercentage: technicians.length === 0 ? 100 : 0,
          qcRatingLevel: 0,
        }),
      [addTechnician, newTechnicians.length, technicians.length]
    );

    return (
      <PosModal
        header={{ title: t('technicians.assigningTechnicians') }}
        open
        onCancel={onCancelEditTechnicians}
        style={{ maxWidth: 1000 }}
        width="100%"
        footer={
          <Row
            gutter={token.marginSM}
            justify="end"
            style={{ paddingTop: token.marginLG }}
          >
            <Col style={{ marginRight: 'auto' }}>
              <Button
                onClick={onCancelEditTechnicians}
                disabled={isSavingChanges}
                color="primary"
                variant="outlined"
              >
                {t('common.buttons.cancel')}
              </Button>
            </Col>
            <Col>
              <Button
                color="primary"
                variant="outlined"
                onClick={onAddNewTechnicianRow}
                disabled={isSavingChanges}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                {t('technicians.addTechnician')}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={confirmHandler}
                loading={isSavingChanges}
                disabled={isSavingChanges || !isFormDirty}
                icon={<FontAwesomeIcon icon={faFloppyDisk} />}
              >
                {t('common.buttons.save')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Typography.Title level={4}>
          <FontAwesomeIcon
            color={token['orange-6']}
            icon={faUserHelmetSafety}
            style={{ marginRight: token.marginSM }}
          />
          {t('technicians.selectTechnicians')}
        </Typography.Title>
        <Typography.Text>
          {t('technicians.chooseTechniciansDesc')}
        </Typography.Text>
        <Typography.Title level={5} style={{ marginTop: token.margin }}>
          {t('technicians.jobInformation')}
        </Typography.Title>
        <Space
          direction="vertical"
          size={0}
          style={{ marginBottom: token.marginLG }}
        >
          {/* Future addition - <Typography.Text>{t('technicians.skillLevel')}</Typography.Text> */}
          <Typography.Text>
            {t('technicians.technicianCapabilities')} {jobType}
          </Typography.Text>
        </Space>
        <TechniciansTable
          technicianList={technicianList}
          isLoadingTechnicianList={isLoadingTechnicianList}
          technicianNotScheduled={technicianNotScheduled}
        />
        {showAlert && (
          <Alert
            type="warning"
            message={t('technicians.effortHint')}
            showIcon
          />
        )}
      </PosModal>
    );
  }
);
